/**
 * Channel compatibility — validações de identifiers por provider.
 * Usado para decidir se um contato pode ser atendido/encaminhado em um canal específico.
 *
 * Exemplos:
 * - Canal WhatsApp exige `identifiers.whatsapp` OU `identifiers.phone`
 * - Canal Email exige `identifiers.email`
 * - Providers sem validador (ex: website-chat, api-webhook) são considerados sempre compatíveis
 */

import { ProviderId, isProviderCategory } from '@troia-v3/types';
import type { AudienceIdentifierType, ContactIdentifiers } from '@troia-v3/types';

export type IdentifierValidator = (identifiers: Partial<ContactIdentifiers>) => boolean;

export const PROVIDER_CONTACT_VALIDATORS: Record<string, IdentifierValidator> = {
  // WhatsApp — aceita whatsapp ou phone
  [ProviderId.WHATSAPP_BUSINESS]: (id) => (id.whatsapp?.length ?? 0) > 0 || (id.phone?.length ?? 0) > 0,
  [ProviderId.GATEWAY_WHATSAPP]:  (id) => (id.whatsapp?.length ?? 0) > 0 || (id.phone?.length ?? 0) > 0,

  // Instagram
  [ProviderId.INSTAGRAM_MESSAGING]: (id) => (id.instagram?.length ?? 0) > 0,

  // Facebook
  [ProviderId.FACEBOOK_MESSENGER]: (id) => (id.facebook?.length ?? 0) > 0,

  // Telegram
  [ProviderId.TELEGRAM_BOT]: (id) => (id.telegram?.length ?? 0) > 0,

  // Email
  [ProviderId.EMAIL_RESEND]:    (id) => (id.email?.length ?? 0) > 0,
  [ProviderId.EMAIL_SMTP]:      (id) => (id.email?.length ?? 0) > 0,
  [ProviderId.EMAIL_SENDGRID]:  (id) => (id.email?.length ?? 0) > 0,
  [ProviderId.EMAIL_SES]:       (id) => (id.email?.length ?? 0) > 0,
  [ProviderId.GMAIL_API]:       (id) => (id.email?.length ?? 0) > 0,

  // Meta (WhatsApp + Instagram + Facebook)
  [ProviderId.META]: (id) =>
    (id.whatsapp?.length ?? 0) > 0 || (id.phone?.length ?? 0) > 0 ||
    (id.instagram?.length ?? 0) > 0 || (id.facebook?.length ?? 0) > 0,
};

/**
 * Verifica se um contato tem os identifiers necessários para o providerId fornecido.
 * Providers sem validador retornam true (compatibilidade aberta).
 */
export function isContactCompatibleWithProvider(
  providerId: string | undefined,
  identifiers: Partial<ContactIdentifiers> | undefined,
): boolean {
  if (!providerId) return true;
  const validator = PROVIDER_CONTACT_VALIDATORS[providerId];
  if (!validator) return true;
  return validator(identifiers ?? {});
}

/**
 * Regra de "com que identificador este canal alcança a pessoa" — a mesma
 * tabela dos validadores acima, só que devolvendo o VALOR em vez de sim/não.
 * Usado na lista de destinatários da campanha: num canal de e-mail aparece o
 * e-mail, num de WhatsApp o telefone, e assim por diante.
 */
type IdentifierPicker = (identifiers: Partial<ContactIdentifiers>) => string | undefined;

const first = (list: string[] | undefined): string | undefined => list?.[0];

const PROVIDER_IDENTIFIER_PICKERS: Record<string, IdentifierPicker> = {
  [ProviderId.WHATSAPP_BUSINESS]: (id) => first(id.phone) ?? first(id.whatsapp),
  [ProviderId.GATEWAY_WHATSAPP]:  (id) => first(id.phone) ?? first(id.whatsapp),
  [ProviderId.INSTAGRAM_MESSAGING]: (id) => first(id.instagram),
  [ProviderId.FACEBOOK_MESSENGER]:  (id) => first(id.facebook),
  [ProviderId.TELEGRAM_BOT]:        (id) => first(id.telegram),
  [ProviderId.EMAIL_RESEND]:   (id) => first(id.email),
  [ProviderId.EMAIL_SMTP]:     (id) => first(id.email),
  [ProviderId.EMAIL_SENDGRID]: (id) => first(id.email),
  [ProviderId.EMAIL_SES]:      (id) => first(id.email),
  [ProviderId.GMAIL_API]:      (id) => first(id.email),
  [ProviderId.META]: (id) =>
    first(id.phone) ?? first(id.whatsapp) ?? first(id.instagram) ?? first(id.facebook),
};

/** Ordem histórica da lista, para quando não há canal ou o provider não tem regra. */
const pickAny: IdentifierPicker = (id) => first(id.phone) ?? first(id.whatsapp) ?? first(id.email);

export function getContactIdentifierForProvider(
  providerId: string | undefined,
  identifiers: Partial<ContactIdentifiers> | undefined,
): string | undefined {
  const picker = (providerId && PROVIDER_IDENTIFIER_PICKERS[providerId]) || pickAny;
  return picker(identifiers ?? {});
}

/** Provider sem regra (widget/chat de site, webhook) não sabe alcançar contato nenhum. */
export function providerHasContactRule(providerId: string | undefined): boolean {
  return !!providerId && providerId in PROVIDER_CONTACT_VALIDATORS;
}

/**
 * Tipo de identificador que um Público (lista importada) precisa ter para
 * casar com o canal — derivado da categoria oficial do provider, não de
 * substring no id (`gmail-api` não contém "email").
 */
export function identifierTypeForCampaignAudience(
  providerId: string | undefined,
): AudienceIdentifierType | undefined {
  if (!providerId) return undefined;
  return isProviderCategory(providerId, 'email') ? 'email' : 'phone';
}
